import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Container, Grid, Paper } from '@mui/material';
import SearchBusinessComponent from './SearchBusinessComponent/SearchBusinessComponet';
import UserLayout from '../UserLayout';
import SEO from '../common/SEO';
import {
    BUSINESS_DIRECTORY_DESCRIPTION_PREFIX,
    BUSINESS_DIRECTORY_TITLE,
    SITE_DESCRIPTION,
} from '../../config/seo.constants';
import PageTitle from '../common/PageTitle';
import {
    getAllBusiness,
    GetAllBusinessRequestPayload,
    GetAllBusinessResult,
    GetSearchBusinessPayload,
    GetSearchBusinessResult,
    getSearchsBusiness,
} from '../../services/business.service';
import { Business } from '../../types/business.type';
import BusinessListComponent from './BusinessListComponent/BusinessListComponet';
import withAuth from '../../hoc/withAuth';
import useCustomerCallRedirect from '../../hooks/useCustomerCallRedirect';
import { useCall } from '../../customer-call-sdk';

const StyledBusinessDirectory = styled.div`
    .searchComponentContentStyle {
        display: flex;
        justify-content: left;
    }
`;
const BusinessListDirectory: React.FC = () => {
    useCustomerCallRedirect();
    const { refreshedAccessToken } = useCall();
    const [businessList, setBusinessList] = useState<Business[] | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [limit, setLimit] = useState(10);
    const [offset, setOffset] = useState(0);
    const [page, setPage] = useState(0);
    const [searchBusinessName, setSearchBusinessName] = useState<string>('');
    const [isSearchButtonEnable, setIsSearchButtonEnable] = useState<boolean>(false);

    const initBusinessList = async () => {
        const payLoad: GetAllBusinessRequestPayload = {
            limit: 10,
            offset: 0,
        };
        const result: GetAllBusinessResult = await getAllBusiness(payLoad);
        if (result.data) {
            setBusinessList(result.data.businessList);
            setTotalCount(result.data.totalCount || 0);
        }
    };
    const fetchBusinessList = async () => {
        const payLoad: GetAllBusinessRequestPayload = {
            limit,
            offset,
        };
        const result: GetAllBusinessResult = await getAllBusiness(payLoad);
        if (result.data) {
            setBusinessList(result.data.businessList);
            setTotalCount(result.data.totalCount || 0);
        }
    };

    const searchBusiness = async (businessName: string) => {
        setSearchBusinessName(businessName);
        const payload: GetSearchBusinessPayload = {
            searchData: businessName,
            offset: 0,
            limit: 10,
        };
        const result: GetSearchBusinessResult = await getSearchsBusiness(payload);
        if (result.data) {
            setBusinessList(result.data.business as Business[]);
            setOffset(0);
            setPage(0);
            setLimit(10);
            setTotalCount(result.data.totalElements);
        }
    };
    const searchBusinessWithPagination = async () => {
        const payload: GetSearchBusinessPayload = {
            searchData: searchBusinessName,
            offset,
            limit,
        };
        const result: GetSearchBusinessResult = await getSearchsBusiness(payload);
        if (result.data) {
            setBusinessList(result.data.business as Business[]);
            setTotalCount(result.data.totalElements);
        }
    };
    const handleChangePage = (event: any, newPage: number) => {
        if (page < newPage) {
            setOffset((prevOffset) => prevOffset + 1);
            setPage(newPage);
        } else {
            setOffset((prevOffset) => prevOffset - 1);
            setPage(newPage);
        }
    };

    const handleChangeRowsPerPage = (event: any) => {
        setLimit(event.target.value);
        setPage(0);
        setOffset(0);
    };
    const checkSearchValue = (event: any) => {
        // when search box value is empty then reset table
        if (event.target.value === '') {
            initBusinessList();
            setSearchBusinessName('');
            setIsSearchButtonEnable(false);
        } else {
            setIsSearchButtonEnable(true);
        }
    };

    useEffect(() => {
        if (!refreshedAccessToken) {
            return;
        }
        if (!searchBusinessName) {
            fetchBusinessList();
        } else {
            searchBusinessWithPagination();
        }
    }, [page, limit, offset, refreshedAccessToken]);

    return (
        <UserLayout>
            <StyledBusinessDirectory>
                <SEO
                    title={BUSINESS_DIRECTORY_TITLE}
                    description={`${BUSINESS_DIRECTORY_DESCRIPTION_PREFIX} - ${SITE_DESCRIPTION}`}
                />
                <Container>
                    <PageTitle>Community Directory</PageTitle>
                    <Paper className="p-4" elevation={2}>
                        <Grid size={{ xs: 12, sm: 12, md: 12 }} className="searchComponentContentStyle">
                            <SearchBusinessComponent
                                searchBusiness={searchBusiness}
                                initBusinessList={initBusinessList}
                                checkSearchValue={checkSearchValue}
                                isSearchButtonEnable={isSearchButtonEnable}
                            />
                        </Grid>
                    </Paper>
                    <Paper className="mt-5" elevation={2}>
                        <BusinessListComponent
                            handleChangePage={handleChangePage}
                            handleChangeRowsPerPage={handleChangeRowsPerPage}
                            totalCount={totalCount}
                            limit={limit}
                            page={page}
                            businessList={businessList}
                        />
                    </Paper>
                </Container>
            </StyledBusinessDirectory>
        </UserLayout>
    );
};

export default withAuth(BusinessListDirectory);
